"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.arrify = exports.baggage = exports.binarySearchInsert = exports.tuple = exports.exclude = exports.omit = exports.randomPick = exports.findClosest = exports.values = exports.keys = exports.pick = exports.has = exports.trueKeys = exports.trueValues = exports.strongMerge = exports.merge = exports.advancedMerge = exports.clone = exports.traverse = exports.isTraversable = exports.set = exports.chunkify = exports.get = exports.last = exports.range = exports.toObject = exports.some = exports.map = void 0;
const misc_1 = require("./lib/misc");
const random_1 = require("./random");
function map(mappingFn, collection) {
    return keys(collection).reduce((acc, k) => Object.assign(acc, { [k]: mappingFn(collection[k], k) }), {});
}
exports.map = map;
function some(mappingFn, collection) {
    return collection instanceof Set
        ? [...collection].some(mappingFn)
        : keys(collection).some((k) => mappingFn(collection[k], k));
}
exports.some = some;
function toObject(collection) {
    const obj = {};
    collection.forEach((item, key) => {
        obj[key] = item;
    });
    return obj;
}
exports.toObject = toObject;
const range = (from, toExclusive) => Array.from({ length: toExclusive - from }, (_, i) => from + i);
exports.range = range;
const last = (arr) => arr[arr.length - 1];
exports.last = last;
function get(obj, ...path) {
    let value = obj;
    for (let i = 0; i < path.length; i++) {
        if (typeof value !== 'object' || value === null) {
            return undefined;
        }
        value = value[path[i]];
    }
    return value;
}
exports.get = get;
function chunkify(arr, size) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}
exports.chunkify = chunkify;
function set(obj, ...path) {
    let field = obj;
    const value = path.pop();
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!field[key]) {
            field[key] = typeof path[i + 1] === 'number' ? [] : {};
        }
        field = field[key];
    }
    field[(0, exports.last)(path)] = value;
    return obj;
}
exports.set = set;
function isTraversable(v) {
    return Boolean(v && typeof v === 'object' && !(v instanceof Date));
}
exports.isTraversable = isTraversable;
function traverse(mappingFn = (v) => v, collection, test = () => true, context = undefined, getContext = () => context) {
    const mapper = ((v, k) => {
        const fieldContext = getContext(v, k, context);
        return isTraversable(v)
            ? test(v, k, fieldContext)
                ? traverse(mappingFn, v, test, fieldContext, getContext)
                : v
            : mappingFn(v, k, fieldContext);
    });
    return Array.isArray(collection)
        ? collection.map(mapper)
        : map(mapper, collection);
}
exports.traverse = traverse;
function clone(col) {
    return traverse((v) => v, col);
}
exports.clone = clone;
function advancedMerge({ mergeArrays, ignore }, col1, ...collections) {
    const [col2, ...rest] = collections;
    if (!col1 || !col2) {
        return col1;
    }
    const clonedCol1 = clone(col1);
    traverse((value, _, path) => {
        if (!(ignore === null || ignore === void 0 ? void 0 : ignore(value))) {
            set(clonedCol1, ...path, value);
        }
    }, col2, (value, _, path) => {
        const aValue = get(clonedCol1, ...path);
        if (aValue && typeof aValue === 'object') {
            if (!mergeArrays && Array.isArray(aValue) && Array.isArray(value)) {
                set(clonedCol1, ...path, [...aValue, ...value]);
                return false;
            }
            return true;
        }
        set(clonedCol1, ...path, value);
        return false;
    }, [], (_, k, path) => [...path, ...(Array.isArray(k) ? k : [k])]);
    return merge(clonedCol1, ...rest);
}
exports.advancedMerge = advancedMerge;
function merge(col1, ...collections) {
    return advancedMerge({}, col1, ...collections);
}
exports.merge = merge;
function strongMerge(col1, ...collections) {
    return advancedMerge({ mergeArrays: true }, col1, ...collections);
}
exports.strongMerge = strongMerge;
function trueValues(collection) {
    return exclude(collection, undefined, null, false);
}
exports.trueValues = trueValues;
function trueKeys(collection) {
    return keys(trueValues(collection));
}
exports.trueKeys = trueKeys;
function has(obj, key) {
    return key in obj;
}
exports.has = has;
function pick(collection, ...picks) {
    return picks.reduce((acc, k) => Object.assign(acc, has(collection, k) ? { [k]: collection[k] } : {}), {});
}
exports.pick = pick;
function keys(collection) {
    return Object.keys(collection);
}
exports.keys = keys;
function values(collection) {
    return Object.values(collection);
}
exports.values = values;
function findClosest(value, array, getValue) {
    return array.reduce((el1, el2) => Math.abs(value - getValue(el2)) < Math.abs(value - getValue(el1))
        ? el2
        : el1);
}
exports.findClosest = findClosest;
function randomPick(collection) {
    const arr = values(collection);
    return arr[(0, random_1.randomInt)(0, arr.length)];
}
exports.randomPick = randomPick;
function omit(collection, ...omits) {
    if (!collection) {
        return undefined;
    }
    const nRec = Object.assign({}, collection);
    omits.forEach((k) => delete nRec[k]);
    return nRec;
}
exports.omit = omit;
function exclude(collection, ...exclusions) {
    const toExclude = new Set([exclusions.shift(), ...exclusions]);
    if (Array.isArray(collection)) {
        return collection.filter((k) => !toExclude.has(k));
    }
    const nRec = Object.assign({}, collection);
    Object.keys(collection).forEach((k) => {
        if (toExclude.has(collection[k])) {
            delete nRec[k];
        }
    });
    return nRec;
}
exports.exclude = exclude;
const tuple = (...args) => args;
exports.tuple = tuple;
function binarySearchInsert(insertion, array, sort, immutable = false) {
    const sortedArray = immutable ? [...array] : array;
    (function insertHelper(start, end) {
        if (start === end) {
            sortedArray.splice(start, 0, insertion);
        }
        else {
            const mid = Math.floor((start + end) / 2);
            const comparison = sort(insertion, sortedArray[mid]);
            if (comparison === 0) {
                sortedArray.splice(mid, 0, insertion);
            }
            else if (comparison < 0) {
                insertHelper(start, mid);
            }
            else {
                insertHelper(mid + 1, end);
            }
        }
    })(0, array.length);
    return sortedArray;
}
exports.binarySearchInsert = binarySearchInsert;
function baggage(dict) {
    return Object.entries(dict)
        .map((entry) => entry.map(encodeURIComponent).join('='))
        .join(',');
}
exports.baggage = baggage;
function arrify(value) {
    return Array.isArray(value) || (0, misc_1.isNullish)(value) ? value : [value];
}
exports.arrify = arrify;
